import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as api from '../../../api';
import styled from 'styled-components';
import Spinner from '../../../Plateforme/Atoms/Spinner';
import { Link } from 'react-router-dom';
import FabFrIndicator from '../../Atoms/FabFrIndicator';
import Popup from '../../Atoms/Popup';
import H2 from '../../Atoms/H2';
import BlueButton from '../../Atoms/BlueButton';
import { getEventDesignerDisplayName, getEventDesignerProfileLink } from '../../../Components/AdminPanels/utils/eventDesignerHelpers';
import { supabase } from '../../../lib/supabaseClient';

const PageContainer = styled.div`
    max-width: 1400px;
    margin: 0 auto;
    padding: 30px;
    background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);
    min-height: 100vh;
`

const Header = styled.div`
    background: white;
    border-radius: 20px;
    padding: 30px 40px;
    margin-bottom: 30px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.08);

    h1 {
        font-size: 28px;
        color: #1a1a2e;
        margin: 0 0 8px 0;
        font-weight: 700;
    }

    h2 {
        font-size: 20px;
        color: #667eea;
        margin: 0 0 16px 0;
        font-weight: 600;
    }

    .jury-info {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        padding: 16px 24px;
        border-radius: 12px;
        font-size: 14px;
        margin-top: 16px;

        .jury-label {
            font-weight: 600;
            margin-bottom: 8px;
        }

        .jury-members {
            opacity: 0.9;
        }
    }
`

const NavLinks = styled.div`
    display: flex;
    gap: 12px;
    margin-bottom: 24px;
    flex-wrap: wrap;

    a button {
        padding: 12px 20px;
        border: none;
        border-radius: 10px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        gap: 8px;

        &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }
    }

    .back-btn {
        background: #f0f0f0;
        color: #333;

        &:hover {
            background: #e0e0e0;
        }
    }

    .compilation-btn {
        background: linear-gradient(135deg, #667eea, #764ba2);
        color: white;
    }

    .projects-btn {
        background: linear-gradient(135deg, #4ade80, #22c55e);
        color: white;
    }
`

const StatsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    margin-bottom: 30px;
`

const StatCard = styled.div`
    background: white;
    border-radius: 16px;
    padding: 24px;
    text-align: center;
    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
    transition: transform 0.2s ease;

    &:hover {
        transform: translateY(-4px);
    }

    .number {
        font-size: 3em;
        font-weight: 800;
        background: ${props => props.gradient || 'linear-gradient(135deg, #667eea, #764ba2)'};
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        line-height: 1;
    }

    .label {
        font-size: 14px;
        color: #666;
        margin-top: 8px;
        font-weight: 500;
    }
`

const FiltersCard = styled.div`
    background: white;
    border-radius: 16px;
    padding: 24px;
    margin-bottom: 24px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
    display: flex;
    gap: 24px;
    align-items: center;
    flex-wrap: wrap;

    .filter-group {
        display: flex;
        align-items: center;
        gap: 12px;

        label {
            font-weight: 600;
            color: #333;
            font-size: 14px;
        }

        select {
            padding: 10px 16px;
            border: 2px solid #e0e0e0;
            border-radius: 8px;
            font-size: 14px;
            background: white;
            cursor: pointer;
            transition: border-color 0.2s;

            &:focus {
                outline: none;
                border-color: #667eea;
            }
        }
    }
`

const ProjectsContainer = styled.div`
    background: white;
    border-radius: 20px;
    padding: 24px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
`

const TableHeader = styled.div`
    display: grid;
    grid-template-columns: 2fr 100px 100px 100px 120px;
    padding: 16px 20px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 12px;
    margin-bottom: 16px;
    color: white;
    font-weight: 600;
    font-size: 14px;

    > div {
        display: flex;
        align-items: center;

        &:not(:first-child) {
            justify-content: center;
        }
    }
`

const RatingLine = styled.div`
    display: grid;
    grid-template-columns: 2fr 100px 100px 100px 120px;
    padding: 16px 20px;
    border-radius: 12px;
    margin-bottom: 8px;
    background: ${props => props.validated ? 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)' : '#f8f9fa'};
    border: 2px solid ${props => props.validated ? '#4caf50' : 'transparent'};
    transition: all 0.2s ease;

    &:hover {
        background: ${props => props.validated ? 'linear-gradient(135deg, #c8e6c9 0%, #a5d6a7 100%)' : '#f0f0f0'};
        transform: translateX(4px);
    }

    .projinfo {
        display: flex;
        flex-direction: column;
        gap: 4px;

        a {
            color: #1a1a2e;
            text-decoration: none;
            transition: color 0.2s;

            &:hover {
                color: #667eea;
            }
        }

        .project-name {
            font-weight: 600;
            font-size: 15px;
            display: flex;
            align-items: center;
            gap: 8px;
        }

        .designer-name {
            font-size: 13px;
            color: #666;
        }
    }

    .stat-cell {
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        font-size: 16px;

        &.stars {
            color: #ffc107;
        }

        &.hearts {
            color: #e91e63;
        }

        &.comments {
            color: #667eea;
        }
    }

    .details-btn {
        display: flex;
        align-items: center;
        justify-content: center;

        button {
            background: linear-gradient(135deg, #667eea, #764ba2);
            color: white;
            border: none;
            padding: 8px 16px;
            border-radius: 8px;
            cursor: pointer;
            font-size: 13px;
            font-weight: 600;
            transition: all 0.2s;

            &:hover {
                transform: scale(1.05);
                box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
            }
        }
    }
`

const DetailsPopup = styled.div`
    .popup-header {
        text-align: center;
        margin-bottom: 24px;

        h2 {
            color: #1a1a2e;
            margin-bottom: 8px;
        }

        .designer {
            color: #666;
            font-size: 16px;
        }
    }

    .ratings-table {
        display: grid;
        grid-template-columns: 2fr 1fr 1fr 3fr;
        gap: 1px;
        background: #e0e0e0;
        border-radius: 12px;
        overflow: hidden;
        margin-bottom: 24px;

        .header-cell {
            background: linear-gradient(135deg, #667eea, #764ba2);
            color: white;
            padding: 14px 16px;
            font-weight: 600;
            font-size: 14px;
        }

        .data-cell {
            background: white;
            padding: 14px 16px;
            display: flex;
            align-items: center;

            &.jury-name {
                flex-direction: column;
                align-items: flex-start;

                .name {
                    font-weight: 600;
                }

                .email {
                    font-size: 12px;
                    color: #888;
                }
            }

            &.stars-cell {
                justify-content: center;
                font-size: 18px;
                color: #ffc107;
            }

            &.heart-cell {
                justify-content: center;
                font-size: 18px;
                color: #e91e63;
            }

            &.comment-cell {
                font-size: 13px;
                color: #555;
                font-style: italic;
            }
        }
    }
`

const EmptyState = styled.div`
    text-align: center;
    padding: 60px 20px;
    color: #888;

    .icon {
        font-size: 60px;
        margin-bottom: 16px;
    }

    .message {
        font-size: 18px;
        font-weight: 500;
    }
`

// Event ID for FD100 2025
const EVENT_ID = '67590da5499bcc995c210b40';

// Contract mention project IDs (same as in MentionContractJuryProfile)
const CONTRACT_PROJECT_IDS = [
    '680fa844a71ddf126b77b977', // CEYSSON & BENETIÈRE GALLERY - Sophie Dries
    '67e57f5143d5609350e3519a', // VILLA - MHNA
    '680113739167c049fff52d73', // FRANCE PAVILLION – EXPO 2025 OSAKA - JOSE LEVY
    '68025d069167c049fff56132', // OFFSIDE BAR - EL'LE
    '67e51ddb43d5609350e33667', // THE ÉCOLE DES ARTS JOAILLIERS - CONSTANCE GUISSET
    '67da910a43d5609350e274d7', // ORIENT EXPRESS CORINTHIAN - ANGEAC
];

const CompilationLine = ({ project, ratings, editedDataMap }) => {
    const { i18n } = useTranslation();
    const [showDetails, setShowDetails] = useState(false);

    // Helper to get display name (edited or original)
    const getDisplayProjectName = () => {
        const edits = editedDataMap?.[project.projet._id];
        return edits?.projectName || project.projet.name_en || project.projet.name;
    };

    // Helper to get display creator name (edited or original)
    const getDisplayCreatorName = () => {
        const edits = editedDataMap?.[project.projet._id];
        return edits?.creatorName || getEventDesignerDisplayName(project.projet, project.user);
    };

    const totalStars = ratings.reduce((total, r) => total + (r.rating.stars || 0), 0);
    const totalHearts = ratings.reduce((total, r) => total + (r.rating.Coup_de_coeur === "checked" ? 1 : 0), 0);
    const totalComments = ratings.reduce((total, r) => total + (r.rating.commentaire ? 1 : 0), 0);

    return (
        <>
            <RatingLine validated={project.projet.statusFinal === "validé"}>
                <div className='projinfo'>
                    <div className="project-name">
                        <a
                            href={"/admin/events/fdficheprojet?email=" + project.user.user.email + "&pid=" + project.user.participation._id + "&projid=" + project.projet._id + "&edit-mode=true"}
                            target="_blank"
                            rel="noreferrer"
                        >
                            {getDisplayProjectName()}
                        </a>
                        {project.projet.fabrication_fr === "oui" && <FabFrIndicator />}
                    </div>
                    <a
                        href={getEventDesignerProfileLink(project.projet, project.user)}
                        target="_blank"
                        rel="noreferrer"
                        className="designer-name"
                    >
                        {getDisplayCreatorName()}
                    </a>
                </div>
                <div className="stat-cell stars">{totalStars} / 3</div>
                <div className="stat-cell hearts">{totalHearts > 0 ? `${totalHearts}` : '-'}</div>
                <div className="stat-cell comments">{totalComments > 0 ? `${totalComments}` : '-'}</div>
                <div className="details-btn">
                    <button onClick={() => setShowDetails(true)}>Voir details</button>
                </div>
            </RatingLine>

            {showDetails && (
                <Popup close={() => setShowDetails(false)} style={{ maxWidth: "900px" }}>
                    <DetailsPopup>
                        <div className="popup-header">
                            <H2>{getDisplayProjectName()}</H2>
                            <div className="designer">{getDisplayCreatorName()}</div>
                        </div>

                        {ratings.length > 0 ? (
                            <div className="ratings-table">
                                <div className="header-cell">Jury</div>
                                <div className="header-cell">Note</div>
                                <div className="header-cell">Coup de Coeur</div>
                                <div className="header-cell">Commentaire</div>

                                {ratings.map((rating, i) => (
                                    <React.Fragment key={i}>
                                        <div className="data-cell jury-name">
                                            <span className="name">{rating.rating?.jury?.first_name} {rating.rating?.jury?.last_name}</span>
                                            <span className="email">{rating.rating?.jury?.email}</span>
                                        </div>
                                        <div className="data-cell stars-cell">
                                            {'★'.repeat(rating.rating?.stars || 0)}{'☆'.repeat(3 - (rating.rating?.stars || 0))}
                                        </div>
                                        <div className="data-cell heart-cell">
                                            {rating.rating?.Coup_de_coeur === "checked" ? "❤️" : "-"}
                                        </div>
                                        <div className="data-cell comment-cell">
                                            {rating.rating?.commentaire || "-"}
                                        </div>
                                    </React.Fragment>
                                ))}
                            </div>
                        ) : (
                            <EmptyState>
                                <div className="message">Aucune note pour ce projet</div>
                            </EmptyState>
                        )}

                        <BlueButton onClick={() => setShowDetails(false)}>Fermer</BlueButton>
                    </DetailsPopup>
                </Popup>
            )}
        </>
    );
};

const FD100MentionContractRatings = () => {
    const [event, setEvent] = useState(null);
    const [allProjects, setAllProjects] = useState([]);
    const [ratings, setRatings] = useState([]);
    const [mentionContractJury, setMentionContractJury] = useState([]);
    const [tri, setTri] = useState('note');
    const [filtre, setFiltre] = useState('');
    const [editedDataMap, setEditedDataMap] = useState({});

    // Parse eid from url
    useEffect(() => {
        const eventIdFromParam = new URLSearchParams(window.location.search).get("eid");
        if (eventIdFromParam) {
            api.getEventById({ id: eventIdFromParam }).then((response) => {
                response.data.displaytype = (response.data.type === "sde" || response.data.type === "sdo") ? "Speed Dating" : "FD 100";
                setEvent(response.data);
            }).catch((error) => {
                console.log(error);
            });
        }
    }, []);

    // Get all participations
    const getAllParticipations = React.useCallback(() => {
        if (!event) return;

        api.getParticipatingUsersWithData({ eventId: event._id }).then((response) => {
            let _allProjects = [];
            response.data.forEach(user => {
                if (user.participation.data.projets) {
                    user.participation.data.projets.forEach(project_id => {
                        const project = user.company.projects.find((projet) => projet._id === project_id);

                        // Only include projects that are in the CONTRACT_PROJECT_IDS list
                        // If list is empty, show all validated projects (for testing)
                        if (project && project.status === "validé") {
                            if (CONTRACT_PROJECT_IDS.length === 0 || CONTRACT_PROJECT_IDS.includes(project._id)) {
                                _allProjects.push({ projet: { ...project, participationId: user.participation._id }, user: user });
                            }
                        }
                    });
                }
            });
            console.log("Contract mention projects:", _allProjects);
            setAllProjects(_allProjects);
        }).catch((error) => {
            console.log(error);
        });
    }, [event]);

    useEffect(() => {
        getAllParticipations();
    }, [getAllParticipations]);

    // Fetch edited data from Supabase for display consistency
    useEffect(() => {
        if (allProjects.length === 0) return;

        const fetchEditedData = async () => {
            const projectIds = allProjects.map(p => p.projet._id);

            try {
                const { data: trackingData, error } = await supabase
                    .from('fd100_laureats_tracking')
                    .select('project_id, edited_project_name, edited_creator_name')
                    .in('project_id', projectIds);

                if (!error && trackingData) {
                    const dataMap = {};
                    trackingData.forEach(record => {
                        dataMap[record.project_id] = {
                            projectName: record.edited_project_name,
                            creatorName: record.edited_creator_name
                        };
                    });
                    console.log('Loaded edited data from Supabase:', dataMap);
                    setEditedDataMap(dataMap);
                }
            } catch (err) {
                console.error('Error fetching edited data from Supabase:', err);
            }
        };

        fetchEditedData();
    }, [allProjects]);

    // Get mention contract jury's ratings from Supabase
    useEffect(() => {
        if (!event) return;

        const fetchVotesFromSupabase = async () => {
            try {
                const { data: votes, error } = await supabase
                    .from('fd100_mention_contract_votes')
                    .select('*');

                if (error) {
                    console.error('Error fetching votes from Supabase:', error);
                    return;
                }

                console.log("Votes from Supabase:", votes);

                if (!votes || votes.length === 0) {
                    console.log("No mention contract votes found");
                    return;
                }

                // Extract unique jury members from votes
                const juryMap = {};
                votes.forEach(vote => {
                    if (!juryMap[vote.user_id]) {
                        juryMap[vote.user_id] = {
                            _id: vote.user_id,
                            email: vote.user_email,
                            first_name: vote.user_name?.split(' ')[0] || '',
                            last_name: vote.user_name?.split(' ').slice(1).join(' ') || ''
                        };
                    }
                });
                setMentionContractJury(Object.values(juryMap));

                // Compile ratings by project
                let _ratings = [];
                votes.forEach(vote => {
                    const rating = {
                        participationId: vote.participation_id,
                        projetId: vote.project_id,
                        stars: vote.stars || 0,
                        Coup_de_coeur: vote.coup_de_coeur ? "checked" : "",
                        commentaire: vote.commentaire || "",
                        jury: juryMap[vote.user_id]
                    };

                    let _projetIndex = _ratings.findIndex(r => r.id === rating.participationId + rating.projetId);
                    if (_projetIndex === -1) {
                        _ratings.push({ id: rating.participationId + rating.projetId, ratings: [{ rating }] });
                    } else {
                        _ratings[_projetIndex].ratings.push({ rating });
                    }
                });

                console.log("Compiled ratings from Supabase:", _ratings);
                setRatings(_ratings);
            } catch (err) {
                console.error('Error fetching votes:', err);
            }
        };

        fetchVotesFromSupabase();
    }, [event]);

    const cumulNoteOfProj = (project) => {
        const _ratings = ratings.find(rating => rating.id === project.projet.participationId + project.projet._id)?.ratings || [];
        return _ratings.reduce((totalStars, rating) => totalStars + (rating.rating.stars || 0), 0);
    };

    const totalMentionsForProj = (project) => {
        const _ratings = ratings.find(rating => rating.id === project.projet.participationId + project.projet._id)?.ratings || [];
        return _ratings.reduce((total, rating) => total + (rating.rating.Coup_de_coeur === "checked" ? 1 : 0), 0);
    };

    const filterByStatus = (project) => {
        if (filtre === '' || (filtre === 'avalider' && !project.projet.statusFinal)) {
            return true;
        }
        return filtre === project.projet.statusFinal;
    };

    const getUniqueParticipationsCount = () => {
        let participations = [];
        allProjects.forEach(project => {
            if (!participations.includes(project.projet.participationId)) {
                participations.push(project.projet.participationId);
            }
        });
        return participations.length;
    };

    const totalVotedProjects = allProjects.filter(project =>
        ratings.find(r => r.id === project.projet.participationId + project.projet._id)?.ratings?.some(r => r.rating.stars > 0)
    ).length;

    const totalFavorites = ratings.reduce((total, r) =>
        total + r.ratings.filter(rating => rating.rating.Coup_de_coeur === "checked").length, 0
    );

    if (!event) {
        return (
            <PageContainer>
                <Spinner />
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <Header>
                <h1>{event.title_fr}</h1>
                <h2>Notes du Jury - Mention Contract</h2>
                {mentionContractJury.length > 0 && (
                    <div className="jury-info">
                        <div className="jury-label">Membres du jury Mention Contract :</div>
                        <div className="jury-members">
                            {mentionContractJury.map(j => `${j.first_name} ${j.last_name} (${j.email})`).join(', ')}
                        </div>
                    </div>
                )}
            </Header>

            <NavLinks>
                <Link to="/admin/events">
                    <button className="back-btn">← Retour aux evenements</button>
                </Link>
                <Link to={`/admin/events/fdcompilationjury?eid=${event._id}`}>
                    <button className="compilation-btn">← Retour à la compilation générale</button>
                </Link>
            </NavLinks>

            <StatsGrid>
                <StatCard gradient="linear-gradient(135deg, #667eea, #764ba2)">
                    <div className="number">{getUniqueParticipationsCount()}</div>
                    <div className="label">Candidats</div>
                </StatCard>
                <StatCard gradient="linear-gradient(135deg, #4ade80, #22c55e)">
                    <div className="number">{allProjects.length}</div>
                    <div className="label">Projets Contract</div>
                </StatCard>
                <StatCard gradient="linear-gradient(135deg, #ffc107, #ff9800)">
                    <div className="number">{totalVotedProjects}</div>
                    <div className="label">Projets votes</div>
                </StatCard>
                <StatCard gradient="linear-gradient(135deg, #e91e63, #c2185b)">
                    <div className="number">{totalFavorites}</div>
                    <div className="label">Coups de coeur</div>
                </StatCard>
            </StatsGrid>

            <FiltersCard>
                <div className="filter-group">
                    <label>Tri :</label>
                    <select value={tri} onChange={e => setTri(e.target.value)}>
                        <option value="note">Par cumul des notes</option>
                        <option value="mention">Par coups de coeur</option>
                        <option value="alpha">Par ordre alphabetique</option>
                    </select>
                </div>
                <div className="filter-group">
                    <label>Filtre :</label>
                    <select value={filtre} onChange={e => setFiltre(e.target.value)}>
                        <option value="">Tous</option>
                        <option value="avalider">A valider</option>
                        <option value="validé">Valides</option>
                        <option value="refusé">Refuses</option>
                    </select>
                </div>
            </FiltersCard>

            <ProjectsContainer>
                <TableHeader>
                    <div>Projet / Designer</div>
                    <div>Notes</div>
                    <div>Coeur</div>
                    <div>Comments</div>
                    <div>Actions</div>
                </TableHeader>

                {allProjects.length === 0 ? (
                    <EmptyState>
                        <div className="icon">📋</div>
                        <div className="message">
                            Aucun projet Contract trouve.
                            {CONTRACT_PROJECT_IDS.length === 0 && " (Liste des IDs de projets non configuree)"}
                        </div>
                    </EmptyState>
                ) : (
                    <>
                        {tri === 'note' && allProjects
                            .filter(filterByStatus)
                            .sort((a, b) => cumulNoteOfProj(b) - cumulNoteOfProj(a))
                            .map((project, j) => (
                                <CompilationLine
                                    key={j}
                                    project={project}
                                    ratings={ratings.find(rating => rating.id === project.projet.participationId + project.projet._id)?.ratings || []}
                                    editedDataMap={editedDataMap}
                                />
                            ))
                        }
                        {tri === 'mention' && allProjects
                            .filter(filterByStatus)
                            .sort((a, b) => totalMentionsForProj(b) - totalMentionsForProj(a))
                            .map((project, j) => (
                                <CompilationLine
                                    key={j}
                                    project={project}
                                    ratings={ratings.find(rating => rating.id === project.projet.participationId + project.projet._id)?.ratings || []}
                                    editedDataMap={editedDataMap}
                                />
                            ))
                        }
                        {tri === 'alpha' && allProjects
                            .filter(filterByStatus)
                            .sort((a, b) => {
                                const getDisplayCreatorName = (project) => {
                                    const edits = editedDataMap?.[project.projet._id];
                                    return edits?.creatorName || getEventDesignerDisplayName(project.projet, project.user);
                                };
                                return getDisplayCreatorName(a).localeCompare(getDisplayCreatorName(b));
                            })
                            .map((project, j) => (
                                <CompilationLine
                                    key={j}
                                    project={project}
                                    ratings={ratings.find(rating => rating.id === project.projet.participationId + project.projet._id)?.ratings || []}
                                    editedDataMap={editedDataMap}
                                />
                            ))
                        }
                    </>
                )}
            </ProjectsContainer>
        </PageContainer>
    );
};

export default FD100MentionContractRatings;
